import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { C } from "../theme";
import { Card } from "./Card";
import { Label } from "./Label";
import { Button } from "./Button";
import { FieldInput } from "./Field";
import { ProductForm } from "./ProductForm";
import { CategoryManager } from "./CategoryManager";
import { ConfirmDialog } from "./ConfirmDialog";
import { money } from "../lib/format";
import { canManageCatalog } from "../lib/access";
import { ApiError } from "../lib/api/client";
import {
  createCategory,
  createProduct,
  deleteCategory,
  deleteProduct,
  listCategories,
  listProducts,
  updateCategory,
  updateProduct,
  uploadProductImage,
} from "../lib/api/endpoints";
import { useSession } from "../lib/session/SessionContext";
import type { CategoryDto, CreateCategoryRequest, CreateProductRequest, ProductDto } from "../types";

const PAGE_SIZE = 20;

interface FormState {
  mode: "create" | "edit";
  product?: ProductDto;
}

export function WarehouseTab() {
  const { accessToken, activeTenant } = useSession();
  const userRole = activeTenant?.roleSlug ?? null;
  const canManage = canManageCatalog(userRole);

  const [products, setProducts] = useState<ProductDto[] | null>(null);
  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProductDto | null>(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const token = accessToken ?? "";

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    if (!token) return;
    listCategories(token)
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setError(null);
    listProducts(token, { page, pageSize: PAGE_SIZE, search })
      .then(result => {
        if (cancelled) return;
        setProducts(result.items);
        setTotalCount(result.totalCount);
      })
      .catch(e => {
        if (cancelled) return;
        setProducts([]);
        setError(e instanceof ApiError ? e.detail ?? "Falha ao carregar produtos" : "Falha ao carregar produtos");
      });
    return () => {
      cancelled = true;
    };
  }, [token, page, search, refreshKey]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const handleSubmit = async (payload: CreateProductRequest, imageFile?: File | null) => {
    if (!form) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      let productId = form.product?.id;
      if (form.mode === "create") {
        const created = await createProduct(token, payload);
        productId = created.id;
        setPage(1);
      } else {
        await updateProduct(token, productId!, payload);
      }
      if (imageFile && productId) await uploadProductImage(token, productId, imageFile);
      setForm(null);
      reload();
    } catch (e) {
      setSubmitError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao salvar produto");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setError(null);
    try {
      await deleteProduct(token, pendingDelete.id);
      setPendingDelete(null);
      reload();
    } catch (e) {
      setPendingDelete(null);
      setError(e instanceof ApiError ? e.detail ?? "Falha ao excluir produto" : "Falha ao excluir produto");
    }
  };

  const refreshCategories = useCallback(() => {
    if (!token) return;
    listCategories(token)
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [token]);

  const handleCreateCategory = async (payload: CreateCategoryRequest) => {
    setSubmitting(true);
    setCategoryError(null);
    try {
      await createCategory(token, payload);
      refreshCategories();
    } catch (e) {
      setCategoryError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao criar categoria");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCategory = async (categoryId: string, payload: CreateCategoryRequest) => {
    setSubmitting(true);
    setCategoryError(null);
    try {
      await updateCategory(token, categoryId, payload);
      refreshCategories();
    } catch (e) {
      setCategoryError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao salvar categoria");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    setSubmitting(true);
    setCategoryError(null);
    try {
      await deleteCategory(token, categoryId);
      refreshCategories();
    } catch (e) {
      setCategoryError(e instanceof ApiError ? e.detail ?? "Falha ao excluir categoria" : "Falha ao excluir categoria");
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <>
      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <Label>Produtos</Label>
          <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            {canManage && (
              <Button variant="outline" onClick={() => setManagerOpen(true)} style={{ fontSize: 12 }}>
                Gerenciar categorias
              </Button>
            )}
            {canManage && <Button onClick={() => setForm({ mode: "create" })}>+ Novo produto</Button>}
          </div>
        </div>

        <div style={{ margin: "12px 0" }}>
          <FieldInput placeholder="Buscar por nome ou SKU" value={searchInput} onChange={e => setSearchInput(e.target.value)} style={{ maxWidth: 320 }} />
        </div>

        {error && (
          <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>
            {error}
          </div>
        )}

        {products === null ? (
          <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>Carregando produtos...</div>
        ) : products.length === 0 ? (
          <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>
            {search ? "Nenhum produto encontrado para essa busca." : "Nenhum produto cadastrado ainda."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 }}>
              <thead>
                <tr style={{ textAlign: "left", color: C.light, fontSize: 11 }}>
                  <th style={{ padding: "6px 4px" }}>PRODUTO</th>
                  <th>CATEGORIA</th>
                  <th>PREÇO</th>
                  <th>CUSTO</th>
                  <th>ESTOQUE</th>
                  <th>STATUS</th>
                  {canManage && <th style={{ textAlign: "right" }}>AÇÕES</th>}
                </tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const category = categories.find(c => c.id === p.categoryId);
                  const baixo = p.minStockAlert != null && p.stockQuantity <= p.minStockAlert;
                  return (
                    <tr key={p.id} style={{ borderTop: `1px solid ${C.faint}` }}>
                      <td style={{ padding: "9px 4px", fontWeight: 600, color: C.ink }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {p.imageUrl ? (
                            <img src={p.imageUrl} alt={p.name} style={{ width: 34, height: 34, borderRadius: 8, objectFit: "cover" }} />
                          ) : (
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: C.faint,
                                color: C.light,
                                fontWeight: 700,
                                fontSize: 13,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {p.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            {p.name}
                            {p.sku && <div style={{ fontSize: 10.5, color: C.light, fontWeight: 400 }}>{p.sku}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ color: C.gray }}>{category?.name ?? "—"}</td>
                      <td style={{ fontWeight: 700, color: C.ink }}>{money(p.price)}</td>
                      <td style={{ color: C.gray }}>{p.costPrice != null ? money(p.costPrice) : "—"}</td>
                      <td style={{ fontWeight: 700, color: baixo ? C.red : C.ink }}>
                        {p.stockQuantity}
                        {baixo ? " ⚠" : ""}
                      </td>
                      <td>
                        {p.isActive ? (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 12, background: C.limeSoft, color: C.ink }}>Ativo</span>
                        ) : (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 12, background: C.faint, color: C.gray }}>Inativo</span>
                        )}
                      </td>
                      {canManage && (
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <div style={{ display: "inline-flex", gap: 6 }}>
                            <Button
                              variant="ghost"
                              onClick={() => setForm({ mode: "edit", product: p })}
                              ariaLabel={`Editar ${p.name}`}
                              title="Editar"
                              style={{ textDecoration: "none", padding: 6, display: "inline-flex", alignItems: "center" }}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => setPendingDelete(p)}
                              ariaLabel={`Deletar ${p.name}`}
                              title="Deletar"
                              style={{ textDecoration: "none", padding: 6, display: "inline-flex", alignItems: "center", color: C.red }}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {products !== null && products.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, marginTop: 12, fontSize: 12.5, color: C.gray }}>
            <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ fontSize: 11.5, padding: "5px 11px" }}>
              ← Anterior
            </Button>
            <span>
              Página {page} de {totalPages} · {totalCount} no total
            </span>
            <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ fontSize: 11.5, padding: "5px 11px" }}>
              Próxima →
            </Button>
          </div>
        )}
      </Card>

      {form && (
        <ProductForm
          mode={form.mode}
          initial={form.product}
          categories={categories}
          submitting={submitting}
          submitError={submitError}
          onSubmit={handleSubmit}
          onCancel={() => {
            setForm(null);
            setSubmitError(null);
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Excluir produto"
          confirmLabel="Excluir"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        >
          Tem certeza que deseja excluir <b style={{ color: C.ink }}>"{pendingDelete.name}"</b>? Essa ação não pode ser desfeita.
        </ConfirmDialog>
      )}

      {managerOpen && (
        <CategoryManager
          categories={categories}
          submitting={submitting}
          submitError={categoryError}
          onCreate={handleCreateCategory}
          onUpdate={handleUpdateCategory}
          onDelete={handleDeleteCategory}
          onClose={() => {
            setManagerOpen(false);
            setCategoryError(null);
          }}
        />
      )}
    </>
  );
}
